import React, { useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { useFeatureFlag } from "configcat-react";

import LeadInfoForm from "../LeadMainInfo/LeadInfoForm";

import { Lead } from "./../../../atoms/lead";
import { plansAtom } from "./../../../atoms/plans";
import { Pilot } from "../LeadManagement";
import { toTitleCase } from "../../../utils/utils";

interface PlanInfoProps {
  closer?: boolean;
  setData: React.Dispatch<React.SetStateAction<Lead>>;
  setErrors: React.Dispatch<React.SetStateAction<string[]>>;
  data: Lead;
  initialPlanInfoData: Lead;
  errors: string[];
  showSubscriptionFlags?: boolean;
  showLevels?: boolean;
  isMonthlyBilling?: boolean;
  isIniAptCmplted?: boolean;
  setAddonsErrors: React.Dispatch<React.SetStateAction<string[]>>;
  setPilotInfo?: React.Dispatch<React.SetStateAction<Pilot | null>>;
}

export interface FormFieldType {
  label?: string;
  zipLabel?: string;
  component?: string;
  placeholder?: string;
  name: string;
  id?: string;
  additionalClassName?: string;
  autoComplete?: string;
  required?: boolean;
  regexp?: RegExp;
  validator?: RegExp;
  min?: Number,
  max?: Number,
  isMulti?: boolean;
  options?: { value: any; label: string }[];
  planId?: any;
  monthly?: boolean;
  mask?: string;
  blockLabel?: string;
  selectName?: string;
  inputName?: string;
  disabled?: boolean;
  selectedValues?: any[];
  isOverwritten?: boolean;
  includedServices?: string[];
  priceInfo?: {
    initialMin?: string;
    initialMax?: string;
    recurringMin?: string;
    recurringMax?: string;
    monthlyMin?: string;
    monthlyMax?: string;
  };
}

export interface SelectOption {
  value: any;
  label: string;
  data: {
    initial: string;
    recurring: string;
  }
}

const canPlanPriceBeEdited = (floor: string, ceiling: string): boolean => {
  return parseFloat(ceiling) > parseFloat(floor) && parseFloat(ceiling) > 0;
};

const PlanInfo = (props: PlanInfoProps): React.JSX.Element => {
  // Config cat
  const { value: subscriptionFlagsValue } = useFeatureFlag("subscriptionFlags", false);
  const { value: levelsValue } = useFeatureFlag("levels", false);

  const plans = useRecoilValue(plansAtom);
  const [formFields, setFormFields] = useState<FormFieldType[]>([]);
  const [planOptions, setPlanOptions] = useState<SelectOption[]>([]);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const getFormFields = (selectedPlan: number) => {
    let currentFormFields: FormFieldType[] = [];

    if(planOptions.length) {
      currentFormFields.push(
        {
          label: "Service type",
          component: "select",
          placeholder: "Select type",
          name: "pr_service_id",
          options: planOptions,
          additionalClassName: "_p-right",
          disabled: props.isIniAptCmplted,
        }
      );
    }

    

    if(selectedPlan) {
      let options: { value: string; label: string; }[] = [];
  
      (plans[selectedPlan as keyof typeof plans]['agreementLengths'] as any[]).map((plan: any) => {
        options.push({
          value: String(plan['length']),
          label: plan['name'],
        });
      });
  
      currentFormFields = currentFormFields.concat([
        {
          label: "Contract length",
          component: "select",
          placeholder: "Select contract length",
          name: "pr_agreement_length",
          options: options,
          additionalClassName: "_p-left",
          disabled: props.isIniAptCmplted
        },      
      ]);

      const planPrices = plans[selectedPlan as keyof typeof plans]['prices'];

      // Initial price field
      currentFormFields = currentFormFields.concat(
        {
          label: "Initial Price",
          component: "price",
          name: "pr_initial_charge",
          placeholder: "0",
          additionalClassName: "_p-right",
          regexp: /^[0-9]*([.][0-9]*)?$/,
          priceInfo: {
            initialMin: planPrices && planPrices[0]['initialMin'],
            initialMax: planPrices && planPrices[0]['initialMax'],
          },
          disabled: props.isIniAptCmplted || !canPlanPriceBeEdited(planPrices[0]['initialMin'], planPrices[0]['initialMax']),
          isOverwritten: true,
        },
      );

      // Recurring/Monthly price field
      currentFormFields = currentFormFields.concat(
        {
          label: props.isMonthlyBilling ? "Monthly Price" : "Recurring Price",
          component: "price",
          name: "pr_service_charge",
          placeholder: "0",
          additionalClassName: "_p-left",
          regexp: /^[0-9]*([.][0-9]*)?$/,
          priceInfo: {
            recurringMin: planPrices && planPrices[0]['recurringMin'],
            recurringMax: planPrices && planPrices[0]['recurringMax'],
            monthlyMin: planPrices && planPrices[0]['monthlyMin'],
            monthlyMax: planPrices && planPrices[0]['monthlyMax'],
          },
          disabled: props.isIniAptCmplted || !canPlanPriceBeEdited(props.isMonthlyBilling ? planPrices && planPrices[0]['monthlyMin'] : planPrices && planPrices[0]['recurringMin'], props.isMonthlyBilling ? planPrices && planPrices[0]['monthlyMax'] : planPrices && planPrices[0]['recurringMax']),
          isOverwritten: true,
        },
      );

      // Get included services from the plan data
      const includedServices = plans[selectedPlan as keyof typeof plans]?.services ?
        plans[selectedPlan as keyof typeof plans]?.services?.map((service) => toTitleCase(service.name)) :
        [];

      // Add included services field with data from the plan only if there are services
      if (includedServices && includedServices.length > 0) {
        currentFormFields.push({
          blockLabel: "Included Services",
          name: "included_services",
          component: "includedServices",
          additionalClassName: "_large-field",
          includedServices: includedServices
        });
      }
    }

    /* added prop for testing */
    if (subscriptionFlagsValue || props.showSubscriptionFlags) {
      currentFormFields.push(
        {
          blockLabel: "Subscription Flags",
          name: "subscription_flags",
          component: "subscription",
          additionalClassName: "_large-field",
          selectName: "subscription_flag",
          inputName: "subscription_notes",
        }
      );
    }

    /* added prop for testing */
    if (selectedPlan && (levelsValue || props.showLevels)) {
      currentFormFields.push({
        blockLabel: "Addons",
        name: "levels",
        component: "addons",
        planId: selectedPlan,
        monthly: props.isMonthlyBilling,
        additionalClassName: "_large-field",
      });
    }

    return currentFormFields;
  };
  
  useEffect(() => {
    const planOptions: SelectOption[] = [];
    for (const key in plans) {
      planOptions.push({
        value: plans[key as keyof typeof plans]['serviceTypeId'],
        label: plans[key as keyof typeof plans]['name'],
        data: {
          initial: plans[key as keyof typeof plans]['prices'] && plans[key as keyof typeof plans]['prices'][0]['initialMax'],
          recurring: plans[key as keyof typeof plans]['prices'] && plans[key as keyof typeof plans]['prices'][0]['recurringMax'],
        }
      });
    }
    setPlanOptions(planOptions);
  }, [plans, subscriptionFlagsValue, levelsValue]);

  useEffect(() => {
    const selectedPlan = props.data.pr_service_id || 0;

    const selectedOption = planOptions.find(option => String(option.value) === String(selectedPlan));
    if (selectedOption && props.data.pr_service_name !== selectedOption.label) {
      props.setData(prevData => ({
        ...prevData,
        pr_service_name: selectedOption.label || "",
      }));
    }

    setFormFields(getFormFields(selectedPlan));

    if (selectedPlan && !(props.data.pr_initial_charge && props.data.pr_service_charge)) {
      props.setData((prevData) => {
        const planPrices = plans[selectedPlan as keyof typeof plans]['prices'];

        return {
          ...prevData,
          pr_initial_charge: planPrices && planPrices[0]['initialMax'], // explicitly set to max value because it is impossible to set negative discount in PestRoutes
          pr_service_charge: props.isMonthlyBilling
            ? planPrices && Math.max(planPrices[0]['monthlyMin'], planPrices[0]['monthlyMax'])
            : planPrices && Math.max(planPrices[0]['recurringMin'], planPrices[0]['recurringMax']),
        };
      });
    }
  }, [planOptions, props.data.pr_service_id]);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    if (props.data.pr_agreement_length
      || props.data.pr_initial_charge
      || props.data.pr_service_charge
    ) {
      const selectedPlan = props.data.pr_service_id || 0;
      const planPrices = plans[selectedPlan as keyof typeof plans]['prices'];

      props.setData((prevData) => ({
        ...prevData,
        pr_agreement_length: 0,
        ...(Number(props.initialPlanInfoData.pr_service_id) === Number(props.data.pr_service_id) ?
            { pr_initial_charge: props.initialPlanInfoData.pr_initial_charge } :
            { pr_initial_charge: planPrices && planPrices[0]['initialMax'] }),
        ...(Number(props.initialPlanInfoData.pr_service_id) === Number(props.data.pr_service_id) ?
            { pr_service_charge: props.initialPlanInfoData.pr_service_charge } :
            { pr_service_charge: props.isMonthlyBilling
              ? planPrices && Math.max(planPrices[0]['monthlyMin'], planPrices[0]['monthlyMax'])
              : planPrices && Math.max(planPrices[0]['recurringMin'], planPrices[0]['recurringMax']) }),
      }));
    }
  }, [props.data.pr_service_id]);

  // This effect runs when the plans data loads or when the selected service type changes
  useEffect(() => {
    // Check if the plans object is properly populated with data
    if (Object.keys(plans).length > 0) {
      // If there's already a selected service ID, use it to find the pilot
      if (props.data.pr_service_id) {
        const serviceIdKey = props.data.pr_service_id.toString();
        if (serviceIdKey in plans) {
          const selectedPlan = plans[serviceIdKey];
          
          // If the selected plan has a pilot object with the required properties, use it
          if (selectedPlan.pilot && 
              typeof selectedPlan.pilot === 'object' && 
              'name' in selectedPlan.pilot && 
              'isActive' in selectedPlan.pilot) {
            
            if (props.setPilotInfo) {
              props.setPilotInfo(selectedPlan.pilot);
            }
          } else {
            if (props.setPilotInfo) {
              props.setPilotInfo(null);
            }
          }
        } else {
          if (props.setPilotInfo) {
            props.setPilotInfo(null);
          }
        }
      } else {
        if (props.setPilotInfo) {
          props.setPilotInfo(null);
        }
      }
    }
  }, [props.data.pr_service_id, plans, props.setPilotInfo]);

  return <LeadInfoForm {...props} formFields={formFields} />;
};

export default PlanInfo;
